import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { onSocketEvent } from '@/hooks/useWebSocket'
import {
  useUserTimezone,
  formatTimeInUserTz,
  formatDateInUserTz,
  toIsoDateInUserTz,
  isTodayInUserTz,
  isYesterdayInUserTz,
} from '@/hooks/useUserTimezone'
import { Send, Search, Circle, Trash2, MoreVertical, ArrowLeft, Mail, Pencil, Check, X, ImageIcon, Users, Users2, Plus, MessageSquare, MessageCircle, Inbox, AtSign } from 'lucide-react'
import { GradientAvatar } from '@/components/dashboard-ui'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import MentionInput, { renderWithMentions } from '@/components/ui/MentionInput'
import type { MentionInputRef } from '@/components/ui/MentionInput'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

interface Contact {
  id: string
  name: string
  email: string
  role: string
  status: string
  avatarUrl: string | null
  lastActive: string | null
  unreadCount: number
  lastMessage: string | null
  lastMessageAt: string | null
}

interface Message {
  id: string
  senderId: string
  recipientId: string
  content: string
  senderName: string
  senderAvatar: string | null
  readAt: string | null
  createdAt: string
  updatedAt?: string | null
}

export default function MessagesPage() {
  const { agent } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedContactId = searchParams.get('contact')
  const { toast } = useToast()
  const userTz = useUserTimezone()

  const handleMentionClick = (userId: string) => {
    navigate(`/team/profile/${userId}`)
  }
  const isAdmin = agent?.role === 'admin'
  const isSupervisor = agent?.role === 'supervisor'
  const canDeleteThread = isAdmin || isSupervisor
  const [contacts, setContacts] = useState<Contact[]>([])
  const [groups, setGroups] = useState<Array<{ id: string; name: string; memberCount: number; lastMessage: string | null; lastMessageAt: string | null; unreadCount: number }>>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [pendingImage, setPendingImage] = useState<string | null>(null)
  const [showNewGroup, setShowNewGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupMembers, setNewGroupMembers] = useState<string[]>([])
  const [creatingGroup, setCreatingGroup] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<MentionInputRef>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedGroupId = searchParams.get('group')

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Only images are supported', variant: 'destructive' })
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Images must be under 5MB', variant: 'destructive' })
      return
    }

    const reader = new FileReader()
    reader.onload = () => setPendingImage(reader.result as string)
    reader.readAsDataURL(file)

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Team members for @mention autocomplete
  const teamMembers = contacts.map(c => ({ id: c.id, name: c.name, email: c.email }))

  const selectedContact = contacts.find(c => c.id === selectedContactId)

  useEffect(() => {
    loadContacts()
    loadGroups()
  }, [])

  const loadGroups = async () => {
    try {
      const res = await api.get('/api/team-messages/groups/list')
      if (res.data.success) setGroups(res.data.groups)
    } catch (err: any) {
      console.warn('Failed to load groups:', err?.message)
      toast({ title: 'Error', description: 'Failed to load message groups', variant: 'destructive' })
    }
  }

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || newGroupMembers.length === 0) return
    setCreatingGroup(true)
    try {
      const res = await api.post('/api/team-messages/groups/create', {
        name: newGroupName.trim(),
        memberIds: newGroupMembers
      })
      if (res.data.success) {
        toast({ title: 'Group created', description: `"${newGroupName.trim()}" is ready` })
        setShowNewGroup(false)
        setNewGroupName('')
        setNewGroupMembers([])
        loadGroups()
        setSearchParams({ group: res.data.conversationId })
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to create group', variant: 'destructive' })
    } finally {
      setCreatingGroup(false)
    }
  }

  const loadGroupMessages = async (groupId: string) => {
    try {
      const res = await api.get(`/api/team-messages/groups/${groupId}/messages`)
      if (res.data.success) setMessages(res.data.messages)
    } catch {
      toast({ title: 'Error', description: 'Failed to load group messages', variant: 'destructive' })
    }
  }

  const handleSendToGroup = async () => {
    if (!newMessage.trim() || !selectedGroupId || sending) return
    setSending(true)
    try {
      const res = await api.post(`/api/team-messages/groups/${selectedGroupId}/send`, { content: newMessage.trim() })
      if (res.data.success) {
        recentSends.current.set(res.data.message.id, Date.now())
        setMessages(prev => [...prev, res.data.message])
        setNewMessage('')
        inputRef.current?.focus()
        loadGroups()
      }
    } catch {
      toast({ title: 'Message failed', description: 'Could not send message. Please try again.', variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    if (selectedGroupId) {
      loadGroupMessages(selectedGroupId)
    } else if (selectedContactId) {
      loadMessages(selectedContactId)
      api.put(`/api/team-messages/${selectedContactId}/read`)
        .then(() => loadContacts())
        .catch((err) => console.warn('Failed to mark messages as read:', err?.message))
    }
  }, [selectedContactId, selectedGroupId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Track recently sent message timestamps to prevent echo duplicates
  const recentSends = useRef<Map<string, number>>(new Map())

  // Listen for real-time messages (including own messages from other devices)
  useEffect(() => {
    return onSocketEvent('team:message', (msg: Message) => {
      // Skip echo of messages we just sent from this device
      // Check by ID first, then by content+sender within 5 seconds
      const isDuplicate = recentSends.current.has(msg.id) ||
        (msg.senderId === agent?.id &&
          Array.from(recentSends.current.entries()).some(([, ts]) => Date.now() - ts < 5000))

      if (isDuplicate) {
        recentSends.current.delete(msg.id)
        return
      }

      const isForCurrentChat = msg.senderId === selectedContactId || msg.recipientId === selectedContactId
      const isForCurrentGroup = selectedGroupId && (msg as any).conversationId === selectedGroupId

      if (isForCurrentChat || isForCurrentGroup) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev
          return [...prev, msg]
        })
        if (msg.senderId === selectedContactId) {
          api.put(`/api/team-messages/${selectedContactId}/read`)
            .then(() => loadContacts())
            .catch((err) => console.warn('Failed to mark messages as read:', err?.message))
          return
        }
      }
      loadContacts()
      loadGroups()
    })
  }, [selectedContactId, selectedGroupId, agent?.id])

  const loadContacts = async () => {
    try {
      const res = await api.get('/api/team-messages/contacts')
      if (res.data.success) {
        setContacts(res.data.contacts)
      }
    } catch (error) {
      console.error('Failed to load contacts:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (contactId: string) => {
    try {
      const res = await api.get(`/api/team-messages/${contactId}?limit=100`)
      if (res.data.success) {
        setMessages(res.data.messages)
      }
    } catch (error) {
      console.error('Failed to load messages:', error)
    }
  }

  const handleSend = async () => {
    const hasText = newMessage.trim()
    const hasImage = pendingImage
    if ((!hasText && !hasImage) || !selectedContactId || sending) return
    setSending(true)
    try {
      // Send image first if staged
      if (hasImage) {
        const imgRes = await api.post(`/api/team-messages/${selectedContactId}`, { content: `[image:${pendingImage}]` })
        if (imgRes.data.success) {
          recentSends.current.set(imgRes.data.message.id, Date.now())
          setMessages(prev => [...prev, imgRes.data.message])
        }
        setPendingImage(null)
      }
      // Then send text if any
      if (hasText) {
        const res = await api.post(`/api/team-messages/${selectedContactId}`, { content: newMessage.trim() })
        if (res.data.success) {
          recentSends.current.set(res.data.message.id, Date.now())
          setMessages(prev => [...prev, res.data.message])
        }
      }
      setNewMessage('')
      inputRef.current?.focus()
      loadContacts()
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const selectContact = (contactId: string) => {
    setSearchParams({ contact: contactId })
  }

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await api.delete(`/api/team-messages/message/${messageId}`)
      setMessages(prev => prev.filter(m => m.id !== messageId))
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to delete message', variant: 'destructive' })
    }
  }

  const handleEditMessage = async (messageId: string) => {
    if (!editText.trim()) return
    try {
      await api.put(`/api/team-messages/message/${messageId}`, { content: editText.trim() })
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: editText.trim(), updatedAt: new Date().toISOString() } : m
      ))
      setEditingId(null)
      setEditText('')
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to edit message', variant: 'destructive' })
    }
  }

  const handleDeleteThread = async () => {
    if (!selectedContactId) return
    try {
      await api.delete(`/api/team-messages/${selectedContactId}/thread`)
      setMessages([])
      loadContacts()
      toast({ title: 'Thread deleted', description: 'Conversation has been removed.' })
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to delete thread', variant: 'destructive' })
    }
  }

  const formatTime = (dateStr: string) => {
    if (isTodayInUserTz(dateStr, userTz)) return formatTimeInUserTz(dateStr, userTz)
    if (isYesterdayInUserTz(dateStr, userTz)) return 'Yesterday'
    return formatDateInUserTz(dateStr, userTz, { month: 'short', day: 'numeric' })
  }

  const isOnline = (contact: Contact) => {
    if (contact.status === 'online') return true
    if (!contact.lastActive) return false
    return (Date.now() - new Date(contact.lastActive).getTime()) < 6 * 60 * 1000
  }

  const filteredContacts = contacts.filter(c =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="flex h-[calc(100dvh-13rem-env(safe-area-inset-bottom))] sm:h-[calc(100dvh-12rem-env(safe-area-inset-bottom))] md:h-[calc(100vh-5rem)] -mt-2 overflow-hidden max-w-full">
      {/* Contact List */}
      <div className={cn(
        'w-80 border-r border-border/50 flex flex-col bg-card/50',
        (selectedContactId || selectedGroupId) ? 'hidden md:flex' : 'flex w-full md:w-80'
      )}>
        <div className="px-4 pt-5 pb-4 border-b border-border/50 bg-gradient-to-b from-card to-card/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4 text-brand-royal" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground leading-none mb-0.5">Inbox</p>
                <h1 className="text-base font-heading font-bold tracking-tight truncate">Team Messages</h1>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-brand-periwinkle/10 hover:text-brand-periwinkle shrink-0"
              onClick={() => setShowNewGroup(!showNewGroup)}
              title="New group chat"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search team members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-background border-border/60 focus-visible:ring-brand-periwinkle/30"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* New Group Creation */}
          {showNewGroup && (
            <div className="p-3 border-b border-border/50 space-y-2 bg-muted/20">
              <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide">New Group</p>
              <Input
                placeholder="Group name..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="h-8 text-sm bg-muted/30 border-border/50"
              />
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {contacts.map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newGroupMembers.includes(c.id)}
                      onChange={(e) => setNewGroupMembers(prev =>
                        e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id)
                      )}
                      className="rounded"
                    />
                    <span className="text-sm">{c.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 h-7 text-xs" onClick={handleCreateGroup} disabled={!newGroupName.trim() || newGroupMembers.length === 0 || creatingGroup}>
                  {creatingGroup ? 'Creating...' : 'Create Group'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowNewGroup(false); setNewGroupName(''); setNewGroupMembers([]) }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Group Conversations */}
          {groups.length > 0 && (
            <div className="p-2 border-b border-border/30">
              <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-wider px-2 mb-1">Groups</p>
              {groups.map(group => (
                <button
                  key={group.id}
                  onClick={() => setSearchParams({ group: group.id })}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150 text-left',
                    selectedGroupId === group.id
                      ? 'bg-primary/10 border border-primary/20'
                      : 'hover:bg-muted/50 border border-transparent'
                  )}
                >
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500/80 to-violet-600 flex items-center justify-center shrink-0">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={cn('text-sm truncate', group.unreadCount > 0 ? 'font-semibold' : 'font-medium')}>{group.name}</span>
                      {group.lastMessageAt && (
                        <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                          {formatTime(group.lastMessageAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-xs text-muted-foreground truncate">
                        {group.lastMessage?.startsWith('[image:') ? 'Image attachment' : group.lastMessage || `${group.memberCount} members`}
                      </p>
                      {group.unreadCount > 0 && (
                        <span className="ml-2 flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full text-[10px] font-bold bg-primary text-white">
                          {group.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* DM Contacts */}
          {loading ? (
            <div className="space-y-1 p-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-[68px] bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="h-12 w-12 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-3">
                <Mail className="h-6 w-6 text-primary/40" />
              </div>
              <p className="text-sm font-medium text-foreground/70">
                {contacts.length === 0 ? 'No team members yet' : 'No matches found'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {contacts.length === 0 ? 'Invite team members to start messaging' : 'Try a different search'}
              </p>
            </div>
          ) : (
            (() => {
              const onlineContacts = filteredContacts.filter(isOnline)
              const offlineContacts = filteredContacts.filter(c => !isOnline(c))

              const renderRow = (contact: Contact) => {
                const online = isOnline(contact)
                const selected = selectedContactId === contact.id
                const lastActiveLabel =
                  !online && contact.lastActive
                    ? `Active ${formatTime(contact.lastActive).toLowerCase()}`
                    : null
                return (
                  <button
                    key={contact.id}
                    onClick={() => selectContact(contact.id)}
                    className={cn(
                      'group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left press-scale',
                      'transition-colors duration-200 [transition-timing-function:var(--ease-out,cubic-bezier(0.23,1,0.32,1))]',
                      selected
                        ? 'bg-gradient-to-r from-brand-periwinkle/15 via-brand-royal/8 to-transparent ring-1 ring-brand-periwinkle/30'
                        : 'hover:bg-muted/40',
                    )}
                  >
                    {/* Selected-row left rail */}
                    {selected && (
                      <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-gradient-to-b from-brand-royal to-brand-periwinkle" />
                    )}
                    <GradientAvatar
                      seed={contact.email || contact.id}
                      name={contact.name}
                      src={contact.avatarUrl}
                      size={40}
                      status={online ? 'online' : null}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={cn(
                          'text-sm truncate',
                          contact.unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
                        )}>
                          {contact.name}
                        </span>
                        {contact.lastMessageAt && (
                          <span className={cn(
                            'text-[10px] tabular-nums flex-shrink-0 shrink-0',
                            contact.unreadCount > 0 ? 'text-brand-periwinkle font-semibold' : 'text-muted-foreground/70',
                          )}>
                            {formatTime(contact.lastMessageAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className={cn(
                          'text-xs truncate',
                          contact.unreadCount > 0 ? 'text-foreground/75' : 'text-muted-foreground/80',
                        )}>
                          {contact.lastMessage?.startsWith('[image:')
                            ? 'Image'
                            : contact.lastMessage || lastActiveLabel || contact.email}
                        </p>
                        {contact.unreadCount > 0 && (
                          <span className="ml-2 flex h-5 min-w-[20px] px-1.5 items-center justify-center rounded-full text-[10px] font-bold bg-gradient-to-br from-brand-royal to-brand-periwinkle text-white shadow-sm shadow-brand-periwinkle/30">
                            {contact.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              }

              const SectionHeader = ({ label, count }: { label: string; count: number }) => (
                <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground/70">
                    {label}
                  </span>
                  <span className="text-[9px] font-bold tabular-nums text-muted-foreground/50">{count}</span>
                  <span className="flex-1 h-px bg-border/40" />
                </div>
              )

              return (
                <div className="px-2 pb-3 space-y-0.5">
                  {onlineContacts.length > 0 && (
                    <>
                      <SectionHeader label="Online" count={onlineContacts.length} />
                      {onlineContacts.map(renderRow)}
                    </>
                  )}
                  {offlineContacts.length > 0 && (
                    <>
                      {onlineContacts.length > 0 && <SectionHeader label="Recently active" count={offlineContacts.length} />}
                      {offlineContacts.map(renderRow)}
                    </>
                  )}
                </div>
              )
            })()
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={cn(
        'flex-1 flex flex-col',
        !selectedContactId && !selectedGroupId && 'hidden md:flex'
      )}>
        {/* Group Chat View */}
        {selectedGroupId && (() => {
          const group = groups.find(g => g.id === selectedGroupId)
          return group ? (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/50 backdrop-blur-sm">
                <Button variant="ghost" size="icon" className="md:hidden -ml-2 h-9 w-9" onClick={() => setSearchParams({})}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-violet-500/80 to-violet-600 flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-sm">{group.name}</p>
                  <p className="text-xs text-muted-foreground">{group.memberCount} members</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="mx-auto h-10 w-10 rounded-full bg-muted/60 flex items-center justify-center">
                        <Users2 className="h-5 w-5 text-muted-foreground/70" strokeWidth={1.75} />
                      </div>
                      <p className="text-sm font-medium text-foreground/60 mt-3">No messages yet</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Send the first message to the group</p>
                    </div>
                  </div>
                ) : (
                  messages.map((msg, i) => {
                    const isMine = msg.senderId === agent?.id
                    const showDate = i === 0 || toIsoDateInUserTz(msg.createdAt, userTz) !== toIsoDateInUserTz(messages[i - 1].createdAt, userTz)
                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="flex justify-center my-4">
                            <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                              {isTodayInUserTz(msg.createdAt, userTz) ? 'Today' : isYesterdayInUserTz(msg.createdAt, userTz) ? 'Yesterday' : formatDateInUserTz(msg.createdAt, userTz, { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        )}
                        <div className={cn('flex items-end gap-2', isMine ? 'justify-end' : 'justify-start')}>
                          {!isMine && (
                            <div className="h-7 w-7 shrink-0 mb-1 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                              <span className="text-[10px] text-white font-semibold">
                                {msg.senderName?.charAt(0)?.toUpperCase() || 'L'}
                              </span>
                            </div>
                          )}
                          <div className={cn(
                            'max-w-[65%] lg:max-w-md rounded-2xl px-4 py-2',
                            isMine ? 'bg-brand-primary text-white rounded-br-md' : 'bg-muted rounded-bl-md'
                          )}>
                            {!isMine && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.senderName}</p>}
                            {msg.content.startsWith('[image:') ? (
                              <img src={msg.content.slice(7, -1)} alt="Image" className="max-w-full rounded-lg max-h-64" />
                            ) : (
                              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{renderWithMentions(msg.content, teamMembers, { isMine, onMentionClick: handleMentionClick })}</p>
                            )}
                            <p className={cn('text-[10px] mt-1', isMine ? 'text-white/60' : 'text-muted-foreground')}>
                              {formatTimeInUserTz(msg.createdAt, userTz)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-3 border-t border-border/50 bg-card/30">
                <div className="flex items-center gap-2">
                  <MentionInput
                    ref={inputRef}
                    placeholder="Message the group... use @ to mention"
                    value={newMessage}
                    onChange={setNewMessage}
                    onSend={handleSendToGroup}
                    teamMembers={teamMembers}
                    autoFocus
                    className="bg-muted/30 border-border/50"
                  />
                  <Button size="icon" onClick={handleSendToGroup} disabled={!newMessage.trim() || sending} className="shrink-0 bg-primary hover:bg-primary/90">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : null
        })()}

        {/* DM Chat View */}
        {!selectedGroupId && selectedContact ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/50 backdrop-blur-sm">
              <Button variant="ghost" size="icon" className="md:hidden -ml-2 h-9 w-9" onClick={() => setSearchParams({})}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="relative flex-shrink-0">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={selectedContact.avatarUrl || undefined} />
                  <AvatarFallback className="bg-gradient-to-br from-primary/80 to-primary text-white text-sm font-semibold">
                    {selectedContact.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {isOnline(selectedContact) && (
                  <Circle className="absolute -bottom-0.5 -right-0.5 h-3 w-3 fill-green-500 text-background stroke-[3]" />
                )}
              </div>
              <div>
                <p className="font-heading font-semibold text-sm">{selectedContact.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  {isOnline(selectedContact) ? (
                    <><span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> Online</>
                  ) : selectedContact.lastActive ? (
                    `Last seen ${formatTime(selectedContact.lastActive)}`
                  ) : 'Offline'}
                </p>
              </div>
              {canDeleteThread && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="ml-auto">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={handleDeleteThread}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Thread
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="mx-auto h-10 w-10 rounded-full bg-muted/60 flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-muted-foreground/70" strokeWidth={1.75} />
                    </div>
                    <p className="text-sm font-medium text-foreground/60 mt-3">No messages yet</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Send a message to start the conversation</p>
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMine = msg.senderId === agent?.id
                  const showDate = i === 0 || toIsoDateInUserTz(msg.createdAt, userTz) !== toIsoDateInUserTz(messages[i - 1].createdAt, userTz)
                  const prevMsg = i > 0 ? messages[i - 1] : null
                  const showHeader = !prevMsg || prevMsg.senderId !== msg.senderId || showDate ||
                    (new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime() > 5 * 60 * 1000)
                  const senderName = isMine ? agent?.name : selectedContact?.name
                  const senderAvatar = isMine ? agent?.avatarUrl : selectedContact?.avatarUrl
                  const senderInitial = senderName?.charAt(0)?.toUpperCase() || '?'

                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex justify-center my-4">
                          <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                            {isTodayInUserTz(msg.createdAt, userTz) ? 'Today' : isYesterdayInUserTz(msg.createdAt, userTz) ? 'Yesterday' : formatDateInUserTz(msg.createdAt, userTz, { month: 'long', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                      )}
                      <div className={cn('group hover:bg-muted/30 rounded-lg px-2 py-0.5 -mx-2', showHeader && 'mt-3')}>
                        {/* Slack-style: avatar + name + time header */}
                        {showHeader && (
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="h-8 w-8 shrink-0 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center overflow-hidden cursor-pointer"
                              onClick={() => !isMine && selectedContact && navigate(`/team/profile/${selectedContact.id}`)}
                            >
                              {senderAvatar ? (
                                <img src={senderAvatar} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-xs text-white font-bold">{senderInitial}</span>
                              )}
                            </div>
                            <span className="text-sm font-heading font-semibold">{senderName}</span>
                            <span className="text-[11px] text-muted-foreground">{formatTimeInUserTz(msg.createdAt, userTz)}</span>
                            {/* Action buttons */}
                            {isMine && (
                              <div className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                <button onClick={() => { setEditingId(msg.id); setEditText(msg.content) }} className="p-1 hover:text-primary focus:opacity-100 rounded" aria-label="Edit message" title="Edit"><Pencil className="h-3 w-3" /></button>
                                {(isAdmin || isSupervisor) && (
                                  <button onClick={() => handleDeleteMessage(msg.id)} className="p-1 hover:text-destructive focus:opacity-100 rounded" aria-label="Delete message" title="Delete"><Trash2 className="h-3 w-3" /></button>
                                )}
                              </div>
                            )}
                            {!isMine && isAdmin && (
                              <button onClick={() => handleDeleteMessage(msg.id)} className="ml-auto p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:text-destructive rounded" aria-label="Delete message" title="Delete"><Trash2 className="h-3 w-3" /></button>
                            )}
                          </div>
                        )}
                        {/* Message content with bubble, indented under avatar */}
                        <div className="pl-10">
                          <div className={cn(
                            'inline-block max-w-[85%] lg:max-w-lg rounded-2xl px-3.5 py-2',
                            isMine
                              ? 'bg-brand-primary text-white rounded-br-md'
                              : 'bg-muted rounded-bl-md'
                          )}>
                            {msg.content.startsWith('[image:') ? (
                              <img
                                src={msg.content.slice(7, -1)}
                                alt="Shared image"
                                className="max-w-xs sm:max-w-sm rounded-lg max-h-64 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(msg.content.slice(7, -1), '_blank')}
                              />
                            ) : editingId === msg.id ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleEditMessage(msg.id)
                                    if (e.key === 'Escape') { setEditingId(null); setEditText('') }
                                  }}
                                  className="flex-1 bg-transparent text-[15px] outline-none min-w-0"
                                  autoFocus
                                />
                                <button onClick={() => handleEditMessage(msg.id)} className="p-0.5 hover:opacity-80"><Check className="h-4 w-4" /></button>
                                <button onClick={() => { setEditingId(null); setEditText('') }} className="p-0.5 hover:opacity-80"><X className="h-4 w-4" /></button>
                              </div>
                            ) : (
                              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{renderWithMentions(msg.content, teamMembers, { isMine, onMentionClick: handleMentionClick })}</p>
                            )}
                          </div>
                          <div>
                            {!showHeader && (
                              <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                                {formatTimeInUserTz(msg.createdAt, userTz)}
                                {msg.updatedAt && msg.updatedAt !== msg.createdAt && ' · edited'}
                              </span>
                            )}
                            {showHeader && msg.updatedAt && msg.updatedAt !== msg.createdAt && (
                              <span className="text-[10px] text-muted-foreground italic">edited</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-border/50 bg-card/30">
              {/* Staged image preview */}
              {pendingImage && (
                <div className="mb-2 relative inline-block">
                  <img src={pendingImage} alt="Preview" className="max-h-32 rounded-lg border border-border" />
                  <button
                    onClick={() => setPendingImage(null)}
                    className="absolute -top-2 -right-2 h-5 w-5 bg-destructive text-white rounded-full flex items-center justify-center text-xs hover:bg-destructive/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="shrink-0 h-10 w-10 text-muted-foreground hover:text-foreground"
                  title="Send image"
                >
                  <ImageIcon className="h-4 w-4" />
                </Button>
                <MentionInput
                  ref={inputRef}
                  placeholder="Type a message... use @ to mention"
                  value={newMessage}
                  onChange={setNewMessage}
                  onSend={handleSend}
                  teamMembers={teamMembers}
                  autoFocus
                  className="bg-muted/30 border-border/50"
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={(!newMessage.trim() && !pendingImage) || sending}
                  className="shrink-0 bg-primary hover:bg-primary/90"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground/50 mt-1 text-center">Images auto-delete after 30 days</p>
            </div>
          </>
        ) : (
          (() => {
            const onlineCount = contacts.filter(isOnline).length
            const unreadDmThreads = contacts.filter(c => c.unreadCount > 0).length
            const unreadGroupThreads = groups.filter(g => g.unreadCount > 0).length
            const unreadThreads = unreadDmThreads + unreadGroupThreads
            const unreadTotal =
              contacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0) +
              groups.reduce((sum, g) => sum + (g.unreadCount || 0), 0)
            const mostRecent = [...contacts]
              .filter(c => c.lastMessageAt)
              .sort((a, b) => new Date(b.lastMessageAt!).getTime() - new Date(a.lastMessageAt!).getTime())[0]
            const totalTeam = contacts.length

            return (
              <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto bg-gradient-to-br from-background via-background to-muted/30">
                <div className="w-full max-w-md">
                  {/* Stacked-bubble illustration */}
                  <div className="relative mx-auto mb-6 h-32 w-48">
                    <div className="absolute left-2 top-6 h-14 w-32 rounded-2xl rounded-bl-md bg-muted border border-border/60 shadow-sm flex items-center px-4 gap-2 opacity-60">
                      <span className="h-1.5 w-12 rounded-full bg-foreground/15" />
                      <span className="h-1.5 w-6 rounded-full bg-foreground/15" />
                    </div>
                    <div className="absolute right-2 top-2 h-14 w-32 rounded-2xl rounded-br-md bg-card border border-border shadow-sm flex items-center justify-end px-4 gap-2 opacity-80">
                      <span className="h-1.5 w-8 rounded-full bg-foreground/15" />
                      <span className="h-1.5 w-14 rounded-full bg-foreground/15" />
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 h-16 w-40 rounded-2xl rounded-bl-md bg-gradient-to-br from-primary to-primary/85 shadow-lg shadow-primary/20 flex items-center px-4 gap-2">
                      <MessageSquare className="h-4 w-4 text-primary-foreground/80 shrink-0" />
                      <div className="flex flex-col gap-1.5">
                        <span className="block h-1.5 w-20 rounded-full bg-primary-foreground/40" />
                        <span className="block h-1.5 w-14 rounded-full bg-primary-foreground/30" />
                      </div>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h2 className="font-heading text-xl font-semibold text-foreground">Your team inbox</h2>
                    <p className="text-sm text-muted-foreground mt-1.5">
                      {unreadThreads > 0
                        ? `${unreadTotal} unread message${unreadTotal === 1 ? '' : 's'} across ${unreadThreads} thread${unreadThreads === 1 ? '' : 's'}.`
                        : 'All caught up. Pick a teammate or start something new.'}
                    </p>
                  </div>

                  {/* Stat row — GitHub-inset card pattern */}
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Online</span>
                      </div>
                      <p className="text-xl font-bold text-foreground tabular-nums">{onlineCount}</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Inbox className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Unread</span>
                      </div>
                      <p className={cn('text-xl font-bold tabular-nums', unreadTotal > 0 ? 'text-primary' : 'text-foreground')}>{unreadTotal}</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Team</span>
                      </div>
                      <p className="text-xl font-bold text-foreground tabular-nums">{totalTeam}</p>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="space-y-2">
                    {mostRecent && (
                      <button
                        onClick={() => selectContact(mostRecent.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border bg-card hover:border-primary/40 hover:bg-primary/[0.02] transition-colors text-left group card-inset"
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={mostRecent.avatarUrl || undefined} />
                          <AvatarFallback className="bg-gradient-to-br from-primary/80 to-primary text-white text-sm font-semibold">
                            {mostRecent.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">Continue with {mostRecent.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {mostRecent.lastMessage?.startsWith('[image:') ? 'Image' : mostRecent.lastMessage || 'Open thread'}
                          </p>
                        </div>
                        <span className="text-[10px] text-muted-foreground/70 shrink-0 group-hover:text-primary/70 transition-colors">
                          {formatTime(mostRecent.lastMessageAt!)}
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowNewGroup(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border bg-transparent hover:border-primary/50 hover:bg-primary/[0.02] transition-colors text-left card-inset"
                    >
                      <div className="h-9 w-9 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0">
                        <Plus className="h-4 w-4 text-violet-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">Start a group chat</p>
                        <p className="text-xs text-muted-foreground">Loop in two or more teammates at once</p>
                      </div>
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 mt-6 text-[11px] text-muted-foreground/70">
                    <AtSign className="h-3 w-3" />
                    <span>Type @ to mention a teammate inside any message</span>
                  </div>
                </div>
              </div>
            )
          })()
        )}
      </div>
    </div>
  )
}
